import React, { useState, useEffect, useRef } from "react";
import "./ExportModal.scss";
import { fetchUserName } from "../exportDashboardToPDF";
import logoImage from "../../assets/images/dark-main-pro.png";
import { normalizePartyNames } from "../TruncateParties";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { getAllGlobalSettingsData } from "../../services/api/GlobalSettingsApi";

const ExportModal = ({
  isOpen,
  onClose,
  onExport,
  dashboardData,
  selectedDate,
}) => {
  const [exportType, setExportType] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orientation, setOrientation] = useState("portrait");
  const [contentType, setContentType] = useState("both");
  const [additionalHearingColumns, setAdditionalHearingColumns] = useState([]);
  const modalRef = useRef(null);
  const { globalSettings } = useGlobalSettings();
  const [freshSettings, setFreshSettings] = useState(null);


  // const [showSelectedDateOption, setShowSelectedDateOption] = useState(false);

  // Get custom columns from global settings
  useEffect(() => {
    if (isOpen) {
      const fetchSettings = async () => {
        try {
          const res = await getAllGlobalSettingsData();
          if (res?.data) {
            setFreshSettings(res.data);
          }
        } catch (error) {
          console.error("Failed to fetch global settings for export:", error);
        }
      };
      fetchSettings();
    }
  }, [isOpen]);

  // 4. Update the logic to use freshSettings (fallback to globalSettings context if needed)
  const effectiveSettings = freshSettings || globalSettings;
  
  const customColumnsEnabled = effectiveSettings?.result?.[0]?.cases?.allowCustomColumns || false;
  const customColumns = effectiveSettings?.result?.[0]?.cases?.columns || [];

  // Available additional columns for hearings - NOW INCLUDING NEW COLUMNS AND CUSTOM COLUMNS
  // Note: Coram is always displayed in Case Info column, so it's not available as an additional option
  const baseHearingColumns = [
    { id: "caseStatus", label: "Case Status", enabled: true },
    { id: "caseDetails", label: "Case Details", enabled: true },
    { id: "advocates", label: "Advocates", enabled: true },
    { id: "categoryDetails", label: "Category Details", enabled: true },
    { id: "actsData", label: "Acts Data", enabled: true },
  ];

  // Add custom columns if enabled (only for "custom" content type)
  const availableHearingColumns = contentType === "custom" && customColumnsEnabled
    ? [
      ...baseHearingColumns,
      ...customColumns.map(col => ({
        id: `custom_${col.name}`,
        label: col.name.charAt(0).toUpperCase() + col.name.slice(1).replace(/([A-Z])/g, ' $1'),
        enabled: true
      }))
    ]
    : baseHearingColumns;

  useEffect(() => {
    if (selectedDate) {
      setExportType("range");
      setStartDate(selectedDate);
      setEndDate(selectedDate);
    } else {
      setStartDate("");
      setEndDate("");
      if (exportType === "selected") {
        setExportType("today");
      }
    }
  }, [selectedDate]);

  const getSelectedDateData = () => {
    if (!selectedDate) return { tasks: [], hearings: [] };
    return dashboardData[selectedDate] || { tasks: [], hearings: [] };
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const getTodayData = () => {
    const today = new Date().toISOString().split("T")[0];
    return dashboardData[today] || { tasks: [], hearings: [] };
  };

  const getRangeData = () => {
    const filtered = {};
    const start = new Date(startDate);
    const end = new Date(endDate);

    Object.keys(dashboardData).forEach((date) => {
      const current = new Date(date);
      if (current >= start && current <= end) {
        filtered[date] = dashboardData[date];
      }
    });
    return filtered;
  };

  const filterDataByContentType = (data) => {
    const filteredData = {};
    Object.keys(data).forEach((date) => {
      const dayData = data[date];
      const filteredDayData = {};

      if (contentType === "tasks") {
        filteredDayData.tasks = dayData.tasks || [];
        filteredDayData.hearings = [];
      } else if (contentType === "hearings") {
        filteredDayData.tasks = [];
        filteredDayData.hearings = dayData.hearings || [];
      } else if (contentType === "custom") {
        // For custom, include hearings (with selected columns) and optionally tasks
        filteredDayData.tasks = dayData.tasks || [];
        filteredDayData.hearings = dayData.hearings || [];
      } else {
        // both
        filteredDayData.tasks = dayData.tasks || [];
        filteredDayData.hearings = dayData.hearings || [];
      }
      filteredData[date] = filteredDayData;
    });
    return filteredData;
  };

  const handleAdditionalColumnToggle = (columnId) => {
    setAdditionalHearingColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleExport = () => {
    let dataToExport;
    let exportDateRange = null; // NEW: Track the date range being exported

    if (exportType === "today") {
      const todayData = getTodayData();
      const todayStr = new Date().toISOString().split("T")[0];
      const todayFormatted = { [todayStr]: todayData };
      dataToExport = todayFormatted;
      exportDateRange = { start: todayStr, end: todayStr }; // NEW
    } else if (exportType === "selected") {
      if (selectedDate) {
        const selectedData = getSelectedDateData();
        dataToExport = { [selectedDate]: selectedData };
        exportDateRange = { start: selectedDate, end: selectedDate }; // NEW
      } else {
        dataToExport = {};
      }
    } else if (exportType === "range") {
      dataToExport = getRangeData();
      exportDateRange = { start: startDate, end: endDate }; // NEW
    } else {
      dataToExport = dashboardData;
    }

    const filteredData = filterDataByContentType(dataToExport);

    // Prepare columns to export
    let columnsToExport = [...additionalHearingColumns];

    // For "hearings" and "both" content types: Auto-include custom columns if enabled and they have values
    if ((contentType === "hearings" || contentType === "both") && customColumnsEnabled) {
      // Add custom columns that have values in the data
      customColumns.forEach(col => {
        const customColumnId = `custom_${col.name}`;
        // Check if this column has values in any hearing
        const hasValue = Object.values(filteredData).some(dayData => {
          return dayData.hearings?.some(hearing => {
            const caseData = hearing.caseDate || hearing;
            const customAttrs = caseData.customAttributes?.columns;
            if (customAttrs && Array.isArray(customAttrs)) {
              const customCol = customAttrs.find(c => c.name === col.name);
              return customCol && customCol.value && String(customCol.value).trim() !== "";
            }
            return false;
          });
        });

        if (hasValue && !columnsToExport.includes(customColumnId)) {
          columnsToExport.push(customColumnId);
        }
      });
    }

    // Pass the date range and globalSettings to the export function
    onExport(
      filteredData,
      orientation,
      contentType,
      columnsToExport,
      exportDateRange,
      effectiveSettings
    ); // MODIFIED
    onClose();
  };

  const handlePrint = async () => {
    let dataToExport;
    let exportDateRange = null; // NEW: Track the date range being exported

    if (exportType === "today") {
      const todayData = getTodayData();
      const todayStr = new Date().toISOString().split("T")[0];
      const todayFormatted = { [todayStr]: todayData };
      dataToExport = todayFormatted;
      exportDateRange = { start: todayStr, end: todayStr }; // NEW
    } else if (exportType === "selected") {
      if (selectedDate) {
        const selectedData = getSelectedDateData();
        dataToExport = { [selectedDate]: selectedData };
        exportDateRange = { start: selectedDate, end: selectedDate }; // NEW
      } else {
        dataToExport = {};
      }
    } else if (exportType === "range") {
      dataToExport = getRangeData();
      exportDateRange = { start: startDate, end: endDate }; // NEW
    } else {
      dataToExport = dashboardData;
    }

    const filteredData = filterDataByContentType(dataToExport);

    // Prepare columns to export (same logic as handleExport)
    let columnsToExport = [...additionalHearingColumns];

    // For "hearings" and "both" content types: Auto-include custom columns if enabled and they have values
    if ((contentType === "hearings" || contentType === "both") && customColumnsEnabled) {
      // Add custom columns that have values in the data
      customColumns.forEach(col => {
        const customColumnId = `custom_${col.name}`;
        // Check if this column has values in any hearing
        const hasValue = Object.values(filteredData).some(dayData => {
          return dayData.hearings?.some(hearing => {
            const caseData = hearing.caseDate || hearing;
            const customAttrs = caseData.customAttributes?.columns;
            if (customAttrs && Array.isArray(customAttrs)) {
              const customCol = customAttrs.find(c => c.name === col.name);
              return customCol && customCol.value && String(customCol.value).trim() !== "";
            }
            return false;
          });
        });

        if (hasValue && !columnsToExport.includes(customColumnId)) {
          columnsToExport.push(customColumnId);
        }
      });
    }

    // UPDATED: Pass exportDateRange and globalSettings to generatePrintContent
    await generatePrintContent(
      filteredData,
      orientation,
      contentType,
      columnsToExport,
      exportDateRange,
      effectiveSettings
    );

    onClose();
  };

  const generatePrintContent = async (
    data,
    orientation,
    contentType,
    additionalColumns = [],
    exportDateRange = null,
    globalSettings = null
  ) => {
    const isPortraitMode = orientation === "portrait";

    // Convert logo to base64 for reliable printing
    const getLogoAsBase64 = () => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL("image/png");
            resolve(dataURL);
          } catch (error) {
            console.error("Error converting logo to base64:", error);
            resolve(null);
          }
        };
        img.onerror = () => {
          console.error("Failed to load logo image");
          resolve(null);
        };
        img.src = logoImage;
      });
    };

    // Wait for logo to be converted to base64
    const logoImageData = await getLogoAsBase64();

    const getCompanyName = () => {
      try {
        const globalSettings = localStorage.getItem("globalSettings");
        const company = localStorage.getItem("company");
        const userData = localStorage.getItem("userData");
        const userProfile = localStorage.getItem("userProfile");

        if (globalSettings) {
          const settings = JSON.parse(globalSettings);
          if (settings.companyName) return settings.companyName;
        }

        if (company) {
          const companyData = JSON.parse(company);
          if (companyData.companyName) return companyData.companyName;
          if (companyData.name) return companyData.name;
        }

        if (userData) {
          const user = JSON.parse(userData);
          if (user.companyName) return user.companyName;
          if (user.company && user.company.name) return user.company.name;
        }

        if (userProfile) {
          const profile = JSON.parse(userProfile);
          if (profile.companyName) return profile.companyName;
          if (profile.company) return profile.company;
        }

        return "LawSuit(Pro)";
      } catch (error) {
        console.error("Error retrieving company name:", error);
        return "LawSuit(Pro)";
      }
    };

    const companyName = getCompanyName();
    const currentDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const currentTime = new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const createdOn = `Created on ${currentDate} ${currentTime}`;
    const timestamp = `${currentDate} ${currentTime}`;

    const formatDate = (dateString) => {
      if (!dateString) return "Not scheduled";
      try {
        let date;

        if (
          dateString.includes("th ") ||
          dateString.includes("st ") ||
          dateString.includes("nd ") ||
          dateString.includes("rd ")
        ) {
          let cleanDate = dateString.replace(/(\d+)(th|st|nd|rd)/, "$1");
          date = new Date(cleanDate);
        } else if (dateString.includes("T")) {
          date = new Date(dateString);
        } else {
          date = new Date(dateString);
        }

        if (isNaN(date.getTime())) return "TBD";

        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        });
      } catch (error) {
        console.error("Date formatting error:", error, dateString);
        return "TBD";
      }
    };

    const formatLongDate = (dateString) => {
      if (!dateString) return "";
      try {
        let date;
        if (
          dateString.includes("th ") ||
          dateString.includes("st ") ||
          dateString.includes("nd ") ||
          dateString.includes("rd ")
        ) {
          let cleanDate = dateString.replace(/(\d+)(th|st|nd|rd)/, "$1");
          date = new Date(cleanDate);
        } else {
          date = new Date(dateString);
        }

        if (isNaN(date.getTime())) {
          return new Date().toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        }

        return date.toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } catch (error) {
        return new Date().toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      }
    };

    const getAdditionalColumnData = (hearing, columnId) => {
      const caseData = hearing.caseDate || hearing;

      // Check if this is a custom column
      const isCustomColumn = columnId.startsWith('custom_');
      if (isCustomColumn) {
        const customFieldName = columnId.replace('custom_', '');
        const customAttributes = caseData.customAttributes;
        if (customAttributes?.columns && Array.isArray(customAttributes.columns)) {
          const customCol = customAttributes.columns.find(col => col.name === customFieldName);
          if (customCol) {
            return {
              value: customCol.value || null,
              type: "customColumn",
              label: customFieldName.charAt(0).toUpperCase() + customFieldName.slice(1).replace(/([A-Z])/g, ' $1')
            };
          }
        }
        return {
          value: null,
          type: "customColumn",
          label: customFieldName.charAt(0).toUpperCase() + customFieldName.slice(1).replace(/([A-Z])/g, ' $1')
        };
      }

      switch (columnId) {
        case "advocates":
          return {
            value: caseData.advocates || null,
            type: "advocates",
          };

        case "coram":
          return {
            value:
              caseData.caseStatus?.["Coram"] ||
              caseData.caseStatus?.["Court Number and Judge"] ||
              null,
            type: "coram",
          };

        case "caseStatus":
          if (caseData.caseStatus) {
            const status = caseData.caseStatus;
            return {
              stageOfCase:
                status["Stage of Case"] || status["Case Stage"] || null,
              benchType: status["Bench Type"] || null,
              judicialBranch: status["Judicial Branch"] || null,
              type: "caseStatus",
            };
          }
          return {
            stageOfCase: null,
            benchType: null,
            judicialBranch: null,
            type: "caseStatus",
          };

        case "caseDetails":
          if (caseData.caseDetails) {
            const details = caseData.caseDetails;
            return {
              filingDate: details["Filing Date"] || null,
              regDate: details["Registration Date"] || null,
              regNumber: details["Registration Number"] || null,
              type: "caseDetails",
            };
          }
          return {
            filingDate: null,
            regDate: null,
            regNumber: null,
            type: "caseDetails",
          };

        case "categoryDetails":
          if (caseData.categoryDetails) {
            const category = caseData.categoryDetails;
            return {
              category: category["Category"] || category.Category || null,
              type: "categoryDetails",
            };
          }
          return { category: null, type: "categoryDetails" };

        case "actsData":
          const actsSource = caseData.actsData || caseData.actsAndSections;
          if (actsSource && Array.isArray(actsSource)) {
            return {
              acts: actsSource,
              type: "actsData",
            };
          }
          return { acts: null, type: "actsData" };

        default:
          return "N/A";
      }
    };

    // Calculate totals from data
    let totalHearings = 0;
    let totalTasks = 0;
    let scheduleDate = "";

    if (typeof data === "object") {
      Object.keys(data).forEach((date) => {
        const dayData = data[date];
        if (dayData && dayData.hearings) {
          totalHearings += dayData.hearings.length;
        }
        if (dayData && dayData.tasks) {
          totalTasks += dayData.tasks.length;
        }
      });
    }

    // Calculate scheduleDate using same logic as PDF
    if (!scheduleDate) {
      if (exportDateRange && exportDateRange.start && exportDateRange.end) {
        const todayStr = new Date().toISOString().split("T")[0];

        if (exportDateRange.start === exportDateRange.end) {
          if (exportDateRange.start === todayStr) {
            scheduleDate = formatLongDate(new Date());
          } else {
            scheduleDate = formatLongDate(exportDateRange.start);
          }
        } else {
          scheduleDate = `${formatLongDate(
            exportDateRange.start
          )} to ${formatLongDate(exportDateRange.end)}`;
        }
      } else {
        const datesInData = Object.keys(data)
          .filter((date) => {
            const dayData = data[date];
            return (
              dayData &&
              (dayData.hearings?.length > 0 || dayData.tasks?.length > 0)
            );
          })
          .sort();

        if (datesInData.length > 0) {
          const startDate = datesInData[0];
          const endDate = datesInData[datesInData.length - 1];
          const todayStr = new Date().toISOString().split("T")[0];

          if (startDate === endDate) {
            if (startDate === todayStr) {
              scheduleDate = formatLongDate(new Date());
            } else {
              scheduleDate = formatLongDate(startDate);
            }
          } else {
            scheduleDate = `${formatLongDate(startDate)} to ${formatLongDate(
              endDate
            )}`;
          }
        } else {
          scheduleDate = formatLongDate(new Date());
        }
      }
    }

    const totalItems = totalHearings + totalTasks;

    // Process data into lists
    let hearingsList = [];
    let tasksList = [];
    let groupedHearingsData = {}; // NEW: Store hearings grouped by date
    const sortedDates = Object.keys(data).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    sortedDates.forEach((date) => {
      const dayData = data[date];
      if (dayData && dayData.hearings && Array.isArray(dayData.hearings)) {
        hearingsList = hearingsList.concat(dayData.hearings);
        // Store grouped data
        if (dayData.hearings.length > 0) {
          groupedHearingsData[date] = dayData.hearings;
        }
      }
      if (dayData && dayData.tasks && Array.isArray(dayData.tasks)) {
        tasksList = tasksList.concat(dayData.tasks);
      }
    });

    // Helper function to check if date is today
    const isTodayDate = (dateString) => {
      if (!dateString) return false;
      const today = new Date().toISOString().split("T")[0];
      const checkDate = new Date(dateString).toISOString().split("T")[0];
      return today === checkDate;
    };

    // Helper function to format date for section header
    const formatDateForHeader = (dateString) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        // Get ordinal suffix for day
        const day = date.getDate();
        const getOrdinalSuffix = (d) => {
          if (d > 3 && d < 21) return "th";
          switch (d % 10) {
            case 1:
              return "st";
            case 2:
              return "nd";
            case 3:
              return "rd";
            default:
              return "th";
          }
        };

        const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
        const month = date.toLocaleDateString("en-GB", { month: "long" });
        const year = date.getFullYear();

        return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month} ${year}`;
      } catch (error) {
        return dateString;
      }
    };

    // Determine if Custom Columns should be shown based on Global Settings
    let customColumnsEnabled = false;
    let customColumnNames = [];
    if (globalSettings) {
      const targetSettings = globalSettings.result?.[0];
      if (targetSettings?.cases?.allowCustomColumns) {
        customColumnsEnabled = true;
        customColumnNames = targetSettings.cases.columns || [];
      }
    }

    // Column width calculation - FIXED FOR LANDSCAPE with custom columns support
    let columnsToShow = [];
    let stackedColumns = [];

    const customCols = customColumnsEnabled
      ? customColumnNames
        .map((col) => `custom_${col.name}`)
        .filter((colId) => additionalColumns.includes(colId)) // Only include what user selected
        .filter((colId) => {
          // Check if any hearing has a value for this specific custom column
          return hearingsList.some((hearing) => {
            const caseData = hearing.caseDate || hearing;
            const customAttrs = caseData.customAttributes?.columns;
            if (customAttrs && Array.isArray(customAttrs)) {
              const customCol = customAttrs.find(
                (c) => c.name === colId.replace("custom_", "")
              );
              return (
                customCol &&
                customCol.value &&
                String(customCol.value).trim() !== ""
              );
            }
            return false;
          });
        })
      : [];

    const regularAdditionalCols = additionalColumns.filter(col => !col.startsWith('custom_'));

    if (isPortraitMode && (regularAdditionalCols.length > 0 || customCols.length > 0)) {
      // Filter out caseStatus, caseDetails, and coram from stackedColumns
      // since they appear in case title column (coram is now compulsory)
      const filteredStackedColumns = regularAdditionalCols.filter(
        (col) =>
          col !== "caseStatus" && col !== "caseDetails" && col !== "coram"
      );

      // Add custom columns to stacked columns in portrait mode
      const allStackedColumns = [...filteredStackedColumns, ...customCols];

      // Only show "Additional" column if there are columns to show
      if (allStackedColumns.length > 0) {
        columnsToShow = [];
        stackedColumns = allStackedColumns;
      } else {
        // No additional column needed - only show base 4 columns
        columnsToShow = [];
        stackedColumns = [];
      }
    } else if (!isPortraitMode) {
      // LANDSCAPE MODE: Always use "Additional" column for custom columns
      // Filter out coram and custom columns from regular columns
      const filteredRegularColumns = regularAdditionalCols.filter(
        (col) => col !== "coram"
      );

      // If we have custom columns, add an "Additional" column
      if (customCols.length > 0) {
        // Custom columns go in stacked "Additional" column in landscape
        columnsToShow = filteredRegularColumns;
        stackedColumns = customCols;
      } else {
        // No custom columns - show regular columns normally
        columnsToShow = filteredRegularColumns;
        stackedColumns = [];
      }
    } else {
      // Fallback
      columnsToShow = [];
      stackedColumns = [];
    }

    // Helper function to generate hearing row HTML
    const generateHearingRowHtml = (hearing, slNo) => {
      const caseData = hearing.caseDate || hearing;
      const courtName = caseData.courtName || "N/A";
      const crNo = caseData.caseTypeAndNumber || "N/A";

      let caseTitle = "";
      if (caseData.parties) {
        caseTitle = normalizePartyNames(caseData.parties);
      }

      const cnrNo = caseData.cnrNumber || hearing.courtRef || "";

      const nextHearing =
        caseData.customUndatedRows?.["Next Hearing Date"] ||
        caseData.caseStatus?.["Next Hearing Date"] ||
        "";
      // Track if next hearing is from customUndatedRows for color coding
      const isCustomNextHearing =
        !!caseData.customUndatedRows?.["Next Hearing Date"];

      // Get Coram data - ALWAYS displayed in case title column (compulsory)
      const coramData = getAdditionalColumnData(hearing, "coram");
      const coramValue = coramData?.value || null;

      const caseStatusData = getAdditionalColumnData(hearing, "caseStatus");
      const caseDetailsData = getAdditionalColumnData(hearing, "caseDetails");

      let caseTitleContent = "";

      if (caseTitle) {
        caseTitleContent += `<div style="font-weight: bold; font-size: 9pt; margin-bottom: 5pt;">${caseTitle}</div>`;
      }

      if (cnrNo) {
        caseTitleContent += `<div style="font-size: 8pt; color: #6c757d; margin-bottom: 5pt;">CNR: ${cnrNo}</div>`;
      }

      // Next Hearing (COMPULSORY - always displayed)
      if (nextHearing) {
        const nextHearingColor = isCustomNextHearing ? "#059669" : "#007bff";
        caseTitleContent += `<div style="font-size: 8pt; color: ${nextHearingColor}; font-weight: ${isCustomNextHearing ? "500" : "normal"
          }; margin-bottom: 5pt;">Next Hearing: ${nextHearing}</div>`;
      }

      // ✅ UPDATED: Changed Coram margin-bottom from 8pt to 5pt to match PDF
      // Coram (COMPULSORY - always displayed)
      if (coramValue) {
        caseTitleContent += `<div style="font-size: 8pt; color: #c0392b; margin-bottom: 5pt;">Coram: ${coramValue}</div>`;
      }

      if (isPortraitMode) {
        if (
          additionalColumns.includes("caseStatus") &&
          caseStatusData &&
          caseStatusData.type === "caseStatus"
        ) {
          const hasAnyStatus =
            caseStatusData.stageOfCase ||
            caseStatusData.benchType ||
            caseStatusData.judicialBranch;

          if (hasAnyStatus) {
            // ✅ UPDATED: Changed margin-top from 5pt to 7pt
            caseTitleContent += `<div style="margin-top: 7pt;">`;
            // ✅ UPDATED: Added margin-bottom: 8pt to section label
            caseTitleContent += `<div style="font-weight: bold; font-size: 7.5pt; color: #34495e; margin-bottom: 8pt;">Case Status:</div>`;

            if (caseStatusData.stageOfCase) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Stage of Case:</div>`;
              // ✅ UPDATED: Added margin-bottom: 4pt to value
              caseTitleContent += `<div style="font-size: 7.5pt; color: #007bff; font-weight: bold; margin-left: 3pt; margin-bottom: 4pt;">${caseStatusData.stageOfCase}</div>`;
            }

            if (caseStatusData.benchType) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Bench Type:</div>`;
              // ✅ UPDATED: Added margin-bottom: 4pt to value
              caseTitleContent += `<div style="font-size: 7.5pt; color: #8e44ad; font-weight: bold; margin-left: 3pt; margin-bottom: 4pt;">${caseStatusData.benchType}</div>`;
            }

            if (caseStatusData.judicialBranch) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Judicial Branch:</div>`;
              // ✅ UPDATED: Added margin-bottom: 4pt to value
              caseTitleContent += `<div style="font-size: 7.5pt; color: #1abc9c; font-weight: bold; margin-left: 3pt; margin-bottom: 4pt;">${caseStatusData.judicialBranch}</div>`;
            }

            caseTitleContent += `</div>`;
          }
        }

        if (
          additionalColumns.includes("caseDetails") &&
          caseDetailsData &&
          caseDetailsData.type === "caseDetails"
        ) {
          const hasAnyDetail =
            caseDetailsData.filingDate ||
            caseDetailsData.regDate ||
            caseDetailsData.regNumber;

          if (hasAnyDetail) {
            // ✅ UPDATED: Changed margin-top from 5pt to 7pt
            caseTitleContent += `<div style="margin-top: 7pt;">`;
            // ✅ UPDATED: Added margin-bottom: 8pt to section label
            caseTitleContent += `<div style="font-weight: bold; font-size: 7.5pt; color: #34495e; margin-bottom: 8pt;">Case Details:</div>`;

            if (caseDetailsData.filingDate) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Filing Date:</div>`;
              // ✅ UPDATED: Added margin-bottom: 4pt to value
              caseTitleContent += `<div style="font-size: 7.5pt; color: #2980b9; font-weight: bold; margin-left: 3pt; margin-bottom: 4pt;">${caseDetailsData.filingDate}</div>`;
            }

            if (caseDetailsData.regDate) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Reg. Date:</div>`;
              // ✅ UPDATED: Added margin-bottom: 4pt to value
              caseTitleContent += `<div style="font-size: 7.5pt; color: #8e44ad; font-weight: bold; margin-left: 3pt; margin-bottom: 4pt;">${caseDetailsData.regDate}</div>`;
            }

            if (caseDetailsData.regNumber) {
              // ✅ UPDATED: Added margin-bottom: 2pt to label
              caseTitleContent += `<div style="font-size: 7pt; color: #6c757d; margin-left: 3pt; margin-bottom: 2pt;">Reg. Number:</div>`;
              // ✅ NO margin-bottom on last item (Reg. Number value)
              caseTitleContent += `<div style="font-size: 7.5pt; color: #27ae60; font-weight: bold; margin-left: 3pt;">${caseDetailsData.regNumber}</div>`;
            }

            caseTitleContent += `</div>`;
          }
        }
      }

      let additionalColumnsHtml = "";

      if (isPortraitMode && stackedColumns.length > 0) {
        let isFirstColumn = true;

        stackedColumns.forEach((colId) => {
          // Skip caseStatus, caseDetails, and coram (they're in case title column)
          if (
            colId === "caseStatus" ||
            colId === "caseDetails" ||
            colId === "coram"
          ) {
            return;
          }

          const data = getAdditionalColumnData(hearing, colId);

          // Use data.label for custom columns, otherwise use availableHearingColumns
          let label;
          if (colId.startsWith('custom_') && data.label) {
            label = data.label;
          } else {
            const column = availableHearingColumns.find((c) => c.id === colId);
            label = column?.label || colId;
          }

          let formattedData = "";

          if (colId === "advocates") {
            if (data.value) {
              formattedData = `<span style="color: #e67e22; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "coram") {
            if (data.value) {
              formattedData = `<span style="color: #c0392b; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "categoryDetails") {
            if (data.category) {
              formattedData = `<span style="color: #1abc9c; font-weight: bold; font-size: 7pt;">${data.category}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "actsData") {
            if (data.acts && data.acts.length > 0) {
              const actsParts = data.acts.map((act) => {
                let actText = "";
                if (act.underAct || act.act) {
                  actText += `<span style="color: #34495e; font-weight: bold; font-size: 7pt;">${act.underAct || act.act
                    }</span>`;
                }
                if (act.underSection || act.section) {
                  actText += `<br/><span style="color: #6c757d; font-size: 7pt;">Section: ${act.underSection || act.section
                    }</span>`;
                }
                return actText;
              });
              formattedData = actsParts.join("<br/><br/>");
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId.startsWith('custom_')) {
            // Handle custom columns
            if (data.value) {
              formattedData = `<span style="color: #007bff; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          }

          additionalColumnsHtml += `<div style="${isFirstColumn ? "" : "margin-top: 5pt;"
            }">`;
          additionalColumnsHtml += `<span style="font-weight: bold; font-size: 7pt; color: #34495e;">${label}:</span><br/>`;
          additionalColumnsHtml += formattedData;
          additionalColumnsHtml += `</div>`;

          isFirstColumn = false;
        });

        if (additionalColumnsHtml) {
          additionalColumnsHtml = `<td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6;">${additionalColumnsHtml}</td>`;
        } else {
          additionalColumnsHtml = `<td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6;"></td>`;
        }
      } else if (columnsToShow.length > 0 || stackedColumns.length > 0) {
        // LANDSCAPE MODE - regular columns get their own cells, custom columns are stacked
        columnsToShow.forEach((colId) => {
          const data = getAdditionalColumnData(hearing, colId);
          let formattedData = "";

          if (colId === "caseStatus") {
            const hasAnyStatus =
              data.stageOfCase || data.benchType || data.judicialBranch;

            if (hasAnyStatus) {
              const parts = [];
              if (data.stageOfCase) {
                parts.push(
                  `<span style="color: #6c757d; font-size: 7pt;">Stage of Case:</span><br/><span style="color: #007bff; font-weight: bold; font-size: 7pt;">${data.stageOfCase}</span>`
                );
              }
              if (data.benchType) {
                parts.push(
                  `<span style="color: #6c757d; font-size: 7pt;">Bench Type:</span><br/><span style="color: #8e44ad; font-weight: bold; font-size: 7pt;">${data.benchType}</span>`
                );
              }
              if (data.judicialBranch) {
                parts.push(
                  `<span style="color: #6c757d; font-size: 7pt;">Judicial Branch:</span><br/><span style="color: #1abc9c; font-weight: bold; font-size: 7pt;">${data.judicialBranch}</span>`
                );
              }
              formattedData = parts.join("<br/>");
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "caseDetails") {
            const parts = [];
            if (data.filingDate) {
              parts.push(
                `<span style="color: #6c757d; font-size: 7pt;">Filing Date:</span><br/><span style="color: #2980b9; font-weight: bold; font-size: 7pt;">${data.filingDate}</span>`
              );
            }
            if (data.regDate) {
              parts.push(
                `<span style="color: #6c757d; font-size: 7pt;">Reg. Date:</span><br/><span style="color: #8e44ad; font-weight: bold; font-size: 7pt;">${data.regDate}</span>`
              );
            }
            if (data.regNumber) {
              parts.push(
                `<span style="color: #6c757d; font-size: 7pt;">Reg. Number:</span><br/><span style="color: #27ae60; font-weight: bold; font-size: 7pt;">${data.regNumber}</span>`
              );
            }
            formattedData =
              parts.length > 0
                ? parts.join("<br/>")
                : '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
          } else if (colId === "advocates") {
            if (data.value) {
              formattedData = `<span style="color: #e67e22; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "coram") {
            if (data.value) {
              formattedData = `<span style="color: #c0392b; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "categoryDetails") {
            if (data.category) {
              formattedData = `<span style="color: #1abc9c; font-weight: bold; font-size: 7pt;">${data.category}</span>`;
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          } else if (colId === "actsData") {
            if (data.acts && data.acts.length > 0) {
              const actsParts = data.acts.map((act) => {
                let actText = "";
                if (act.underAct || act.act) {
                  actText += `<span style="color: #34495e; font-weight: bold; font-size: 7pt;">${act.underAct || act.act
                    }</span>`;
                }
                if (act.underSection || act.section) {
                  actText += `<br/><span style="color: #6c757d; font-size: 7pt;">Section: ${act.underSection || act.section
                    }</span>`;
                }
                return actText;
              });
              formattedData = actsParts.join("<br/><br/>");
            } else {
              formattedData =
                '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
            }
          }

          additionalColumnsHtml += `<td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6;">${formattedData}</td>`;
        });

        // Add stacked custom columns in "Additional" column for landscape mode
        if (!isPortraitMode && stackedColumns.length > 0) {
          let stackedHtml = "";
          let isFirstColumn = true;

          stackedColumns.forEach((colId) => {
            // In landscape mode, only show custom columns in Additional column
            if (colId.startsWith('custom_')) {
              const data = getAdditionalColumnData(hearing, colId);
              const label = data.label || colId;

              let formattedData = "";
              if (data.value) {
                formattedData = `<span style="color: #007bff; font-weight: bold; font-size: 7pt;">${data.value}</span>`;
              } else {
                formattedData = '<span style="color: #6c757d; font-size: 7pt;">N/A</span>';
              }

              stackedHtml += `<div style="${isFirstColumn ? "" : "margin-top: 5pt;"
                }">`;
              stackedHtml += `<span style="font-weight: bold; font-size: 7pt; color: #34495e;">${label}:</span><br/>`;
              stackedHtml += formattedData;
              stackedHtml += `</div>`;

              isFirstColumn = false;
            }
          });

          if (stackedHtml) {
            additionalColumnsHtml += `<td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6;">${stackedHtml}</td>`;
          }
        }
      }

      return `
      <tr>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${slNo}</td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${crNo}</td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${courtName}</td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 9pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${caseTitleContent}</td>
        ${additionalColumnsHtml}
      </tr>
    `;
    };

    // Generate additional header columns HTML - must be defined before generating hearings sections
    let additionalHeadersHtml = "";
    if (isPortraitMode && stackedColumns.length > 0) {
      additionalHeadersHtml = "<th>Additional</th>";
    } else if (columnsToShow.length > 0 || stackedColumns.length > 0) {
      additionalHeadersHtml = columnsToShow
        .map((colId) => {
          let label;
          if (colId.startsWith('custom_')) {
            // For custom columns, format the label properly
            const fieldName = colId.replace('custom_', '');
            label = fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1');
          } else {
            const column = availableHearingColumns.find((c) => c.id === colId);
            label = column?.label || colId;
          }

          return `<th>${label}</th>`;
        })
        .join("");

      // Add "Additional" column header in landscape if there are stacked columns (custom columns)
      if (!isPortraitMode && stackedColumns.length > 0) {
        additionalHeadersHtml += "<th>Additional</th>";
      }
    }

    const getTableClass = () => {
      if (isPortraitMode) {
        return stackedColumns.length > 0
          ? "hearings-table"
          : "hearings-table-no-additional";
      } else {
        // Landscape mode - distinguish between normal (<=2 cols) and split (>2 cols)
        const shouldSplitLandscape = columnsToShow.length > 2;
        if (shouldSplitLandscape) {
          return `hearings-table-landscape-split cols-${columnsToShow.length}`;
        } else {
          return `hearings-table-landscape-normal cols-${columnsToShow.length}`;
        }
      }
    };

    const tableClass = getTableClass();

    // Generate hearings HTML - grouped by date if multiple dates
    let hearingsSectionsHtml = "";
    if (
      (contentType === "hearings" ||
        contentType === "both" ||
        contentType === "custom") &&
      hearingsList.length > 0
    ) {
      const groupedDates = Object.keys(groupedHearingsData).sort(
        (a, b) => new Date(a) - new Date(b)
      );

      // Check if we have multiple dates
      if (groupedDates.length > 1) {
        // Multiple dates - generate grouped sections
        let globalSerialNumber = 1;

        for (const dateKey of groupedDates) {
          const dateHearings = groupedHearingsData[dateKey];
          if (!dateHearings || dateHearings.length === 0) continue;

          // Determine section header title
          let sectionTitle;
          if (isTodayDate(dateKey)) {
            sectionTitle = `Today's Hearings`;
          } else {
            sectionTitle = `Hearings for ${formatDateForHeader(dateKey)} (${dateHearings.length
              })`;
          }

          // Generate rows for this date
          let dateHearingsHtml = "";
          for (const hearing of dateHearings) {
            dateHearingsHtml += generateHearingRowHtml(
              hearing,
              String(globalSerialNumber)
            );
            globalSerialNumber++;
          }

          // Add section for this date
          hearingsSectionsHtml += `
          <div class="section-header">
            <span class="section-icon">📅</span>
            ${sectionTitle}
          </div>
          <table class="${tableClass}">
            <thead class="table-header">
              <tr>
                <th>SL No</th>
                <th>CR No</th>
                <th>Court Name</th>
                <th>Case Info</th>
                ${additionalHeadersHtml}
              </tr>
            </thead>
            <tbody>
              ${dateHearingsHtml}
            </tbody>
          </table>
        `;
        }
      } else {
        // Single date or no grouped data
        let sectionTitle;
        if (groupedDates.length === 1 && isTodayDate(groupedDates[0])) {
          sectionTitle = `Today's Hearings`;
        } else if (groupedDates.length === 1) {
          sectionTitle = `Hearings for ${formatDateForHeader(groupedDates[0])}`;
        } else {
          sectionTitle = `Today's Hearings`;
        }

        let hearingsHtml = "";
        let serialNumber = 1;
        for (const hearing of hearingsList) {
          hearingsHtml += generateHearingRowHtml(hearing, String(serialNumber));
          serialNumber++;
        }

        hearingsSectionsHtml = `
        <div class="section-header">
          <span class="section-icon">📅</span>
          ${sectionTitle}
        </div>
        <table class="${tableClass}">
          <thead class="table-header">
            <tr>
              <th>SL No</th>
              <th>CR No</th>
              <th>Court Name</th>
              <th>Case Info</th>
              ${additionalHeadersHtml}
            </tr>
          </thead>
          <tbody>
            ${hearingsHtml}
          </tbody>
        </table>
      `;
      }
    }

    let tasksHtml = "";
    if (
      (contentType === "tasks" ||
        contentType === "both" ||
        contentType === "custom") &&
      tasksList.length > 0
    ) {
      for (const task of tasksList) {
        const courtRef = task.courtRef || "INTERNAL MATTER";
        const createdAt = formatDate(task.createdAt);
        const taskName = task.taskName || task.title || "Untitled Task";

        const assignedToNames =
          task.assignedTo && Array.isArray(task.assignedTo)
            ? await Promise.all(task.assignedTo.map((id) => fetchUserName(id)))
            : ["Unassigned"];

        const assignedToName = assignedToNames[0];
        const dueDate = formatDate(task.dueDate);
        const status = task.isCompleted ? "COMPLETED" : "PENDING";

        const taskDetails = `<div style="font-weight: bold; font-size: 10pt; margin-bottom: 3pt;">${taskName}</div>
                         <div style="font-size: 8pt; color: #6c757d;">Assigned to: ${assignedToName}</div>
                         <div style="font-size: 8pt; color: ${task.isCompleted ? "#007bff" : "#dc3545"
          };">Due: ${dueDate}</div>`;

        tasksHtml += `
      <tr>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 10pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${courtRef}</td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 10pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${createdAt}</td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 10pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">
          <div style="display: inline-block; padding: 3pt 8pt; border-radius: 3pt; background-color: ${task.isCompleted ? "#28a745" : "#f8d7da"
          }; color: ${task.isCompleted ? "#fff" : "#721c24"
          }; font-weight: bold; font-size: 8pt; margin-bottom: 5pt;">${status}</div>
        </td>
        <td style="border: 0.5pt solid #000; padding: 6pt 6pt; font-size: 10pt; font-family: helvetica, sans-serif; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word;">${taskDetails}</td>
      </tr>
    `;
      }
    }

    let noDataHtml = "";
    if (hearingsList.length === 0 && tasksList.length === 0) {
      noDataHtml = `<div class="no-data">No hearings or tasks scheduled for the selected date(s).</div>`;
    }

    // CRITICAL: Match the NEW PDF header format with logo
    let htmlContent = `
  <!DOCTYPE html>
  <html>
    <head>
      <style>
        @page {
          size: ${orientation === "landscape" ? "A4 landscape" : "A4 portrait"};
          margin: 20pt 20pt 40pt 20pt; 

          @bottom-left {
            content: "${timestamp}";
            font-family: helvetica, sans-serif;
            font-size: 9pt;
            font-weight: normal;
            color: #6c757d;
          }

          @bottom-right {
            content: "Page " counter(page);
            font-family: helvetica, sans-serif;
            font-size: 9pt;
            font-weight: normal;
            color: #6c757d;
          }
        }
        
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        body {
          font-family: helvetica, sans-serif;
          font-size: 10pt;
          color: #000;
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background-color: white;
        }
        
        /* NEW MODERN HEADER - Matches your actual PDF */
        .modern-header {
          padding: 0pt 15pt;
          margin-bottom: 15pt;
          border-bottom: 1pt solid #c8d2dc;
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5pt;
        }

        .header-logo {
          font-size: 18pt;
          font-weight: bold;
          color: #0066cc;
        }

        .header-license {
          font-size: 11pt;
          color: #6c757d;
          font-style: italic;
        }

        .header-tagline {
          font-size: 8pt;
          color: #6c757d;
          margin-top: 2pt;
        }
        
        /* Modern Overview Section */
        .overview-section {
          background-color: #f5f7fa;
          padding: 20pt;
          margin-bottom: 20pt;
          border: 1pt solid #c8d2dc;
          border-top : 3pt solid #007bff;
          text-align: center;
        }

        .overview-title {
          font-size: 18pt;
          font-weight: bold;
          color: #004A9A;
          margin-bottom: 15pt;
        }

        .overview-stats {
          font-size: 12pt;
          font-weight: bold;
          color: #000;
          margin-bottom: 8pt;
        }

        .overview-date {
          font-size: 11pt;
          color: #6c757d;
        }
        
        .section-header {
  background-color: #007bff;
  color: white;
  padding: 8pt 10pt;
  font-size: 11pt;
  font-weight: bold;
  margin-top: 15pt;  /* ✅ INCREASED from 10pt to 15pt for better separation */
  margin-bottom: 3pt;  /* ✅ REDUCED from 5pt to 3pt */
  display: flex;
  align-items: center;
  page-break-after: avoid;  /* ✅ NEW */
  page-break-inside: avoid;  /* ✅ NEW */
}

.section-header + table {
  page-break-before: avoid;
}


thead {
  display: table-header-group;
  page-break-after: avoid;
}


table thead + tbody tr:first-child {
  page-break-before: avoid;
}

        .section-icon {
          margin-right: 8pt;
        }

        .task-section-header {
  background-color: #28a745;
  color: white;
  padding: 8pt 10pt;
  font-size: 11pt;
  font-weight: bold;
  margin-top: 20pt;  /* ✅ INCREASED from 16pt */
  margin-bottom: 3pt;  /* ✅ REDUCED from 5pt */
  display: flex;
  align-items: center;
  page-break-after: avoid;  /* ✅ NEW */
  page-break-inside: avoid;  /* ✅ NEW */
}

@media print {
  .section-header,
  .task-section-header {
    orphans: 3; 
    widows: 3; 
  }
  
  .section-header + table,
  .task-section-header + table {
    page-break-before: avoid;
  }
  
  /* Prevent header appearing alone at bottom */
  .section-header {
    min-height: 28pt;  /* Ensure consistent height */
  }
}
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10pt;
          table-layout: fixed;
        }
        
        /* Fixed column widths for hearings table - matching PDF export */
        /* Portrait with additional column: [45, 85, 110, 180, 80] = 500pt */
        .hearings-table th:nth-child(1), .hearings-table td:nth-child(1) { width: 9%; }   /* SL No - 45pt */
        .hearings-table th:nth-child(2), .hearings-table td:nth-child(2) { width: 17%; }  /* CR No - 85pt */
        .hearings-table th:nth-child(3), .hearings-table td:nth-child(3) { width: 22%; }  /* Court Name - 110pt */
        .hearings-table th:nth-child(4), .hearings-table td:nth-child(4) { width: 36%; }  /* Case Info - 180pt */
        .hearings-table th:nth-child(5), .hearings-table td:nth-child(5) { width: 16%; }  /* Additional - 80pt */
        
        /* Portrait without additional column: [45, 100, 115, 240] = 500pt */
        .hearings-table-no-additional th:nth-child(1), .hearings-table-no-additional td:nth-child(1) { width: 9%; }   /* SL No - 45pt */
        .hearings-table-no-additional th:nth-child(2), .hearings-table-no-additional td:nth-child(2) { width: 20%; }  /* CR No - 100pt */
        .hearings-table-no-additional th:nth-child(3), .hearings-table-no-additional td:nth-child(3) { width: 23%; }  /* Court Name - 115pt */
        .hearings-table-no-additional th:nth-child(4), .hearings-table-no-additional td:nth-child(4) { width: 48%; }  /* Case Info - 240pt */
        
        /* LANDSCAPE NORMAL MODE (<=2 additional columns) */
        .hearings-table-landscape-normal {
          width: 100%;
          table-layout: fixed;
        }
        
        .hearings-table-landscape-normal th:nth-child(1),
        .hearings-table-landscape-normal td:nth-child(1) {
          width: 50pt;
          min-width: 50pt;
        }
        
        .hearings-table-landscape-normal th:nth-child(2),
        .hearings-table-landscape-normal td:nth-child(2) {
          width: 110pt;
          min-width: 110pt;
        }
        
        /* 0 additional columns: split remaining between Court Name and Case Info */
        .hearings-table-landscape-normal.cols-0 th:nth-child(3),
        .hearings-table-landscape-normal.cols-0 td:nth-child(3) {
          width: calc((100% - 160pt) / 2);
        }
        
        .hearings-table-landscape-normal.cols-0 th:nth-child(4),
        .hearings-table-landscape-normal.cols-0 td:nth-child(4) {
          width: calc((100% - 160pt) / 2);
        }
        
        /* 1 additional column: (100% - 160pt) / 3 each */
        .hearings-table-landscape-normal.cols-1 th:nth-child(3),
        .hearings-table-landscape-normal.cols-1 td:nth-child(3),
        .hearings-table-landscape-normal.cols-1 th:nth-child(4),
        .hearings-table-landscape-normal.cols-1 td:nth-child(4),
        .hearings-table-landscape-normal.cols-1 th:nth-child(5),
        .hearings-table-landscape-normal.cols-1 td:nth-child(5) {
          width: calc((100% - 160pt) / 3);
        }
        
        /* 2 additional columns: (100% - 160pt) / 4 each */
        .hearings-table-landscape-normal.cols-2 th:nth-child(3),
        .hearings-table-landscape-normal.cols-2 td:nth-child(3),
        .hearings-table-landscape-normal.cols-2 th:nth-child(4),
        .hearings-table-landscape-normal.cols-2 td:nth-child(4),
        .hearings-table-landscape-normal.cols-2 th:nth-child(5),
        .hearings-table-landscape-normal.cols-2 td:nth-child(5),
        .hearings-table-landscape-normal.cols-2 th:nth-child(6),
        .hearings-table-landscape-normal.cols-2 td:nth-child(6) {
          width: calc((100% - 160pt) / 4);
        }
        
        /* LANDSCAPE SPLIT MODE (>2 additional columns) */
        .hearings-table-landscape-split {
          width: 100%;
          table-layout: fixed;
        }
        
        .hearings-table-landscape-split th:nth-child(1),
        .hearings-table-landscape-split td:nth-child(1) {
          width: 50pt;
          min-width: 50pt;
        }
        
        .hearings-table-landscape-split th:nth-child(2),
        .hearings-table-landscape-split td:nth-child(2) {
          width: 90pt;  /* NARROWER than normal mode! */
          min-width: 90pt;
        }
        
        /* 3 additional columns: (100% - 140pt) / 5 each */
        .hearings-table-landscape-split.cols-3 th:nth-child(3),
        .hearings-table-landscape-split.cols-3 td:nth-child(3),
        .hearings-table-landscape-split.cols-3 th:nth-child(4),
        .hearings-table-landscape-split.cols-3 td:nth-child(4),
        .hearings-table-landscape-split.cols-3 th:nth-child(5),
        .hearings-table-landscape-split.cols-3 td:nth-child(5),
        .hearings-table-landscape-split.cols-3 th:nth-child(6),
        .hearings-table-landscape-split.cols-3 td:nth-child(6),
        .hearings-table-landscape-split.cols-3 th:nth-child(7),
        .hearings-table-landscape-split.cols-3 td:nth-child(7) {
          width: calc((100% - 140pt) / 5);
        }
        
        /* 4 additional columns: (100% - 140pt) / 6 each */
        .hearings-table-landscape-split.cols-4 th:nth-child(3),
        .hearings-table-landscape-split.cols-4 td:nth-child(3),
        .hearings-table-landscape-split.cols-4 th:nth-child(4),
        .hearings-table-landscape-split.cols-4 td:nth-child(4),
        .hearings-table-landscape-split.cols-4 th:nth-child(5),
        .hearings-table-landscape-split.cols-4 td:nth-child(5),
        .hearings-table-landscape-split.cols-4 th:nth-child(6),
        .hearings-table-landscape-split.cols-4 td:nth-child(6),
        .hearings-table-landscape-split.cols-4 th:nth-child(7),
        .hearings-table-landscape-split.cols-4 td:nth-child(7),
        .hearings-table-landscape-split.cols-4 th:nth-child(8),
        .hearings-table-landscape-split.cols-4 td:nth-child(8) {
          width: calc((100% - 140pt) / 6);
        }
        
        /* 5 additional columns: (100% - 140pt) / 7 each */
        .hearings-table-landscape-split.cols-5 th:nth-child(3),
        .hearings-table-landscape-split.cols-5 td:nth-child(3),
        .hearings-table-landscape-split.cols-5 th:nth-child(4),
        .hearings-table-landscape-split.cols-5 td:nth-child(4),
        .hearings-table-landscape-split.cols-5 th:nth-child(5),
        .hearings-table-landscape-split.cols-5 td:nth-child(5),
        .hearings-table-landscape-split.cols-5 th:nth-child(6),
        .hearings-table-landscape-split.cols-5 td:nth-child(6),
        .hearings-table-landscape-split.cols-5 th:nth-child(7),
        .hearings-table-landscape-split.cols-5 td:nth-child(7),
        .hearings-table-landscape-split.cols-5 th:nth-child(8),
        .hearings-table-landscape-split.cols-5 td:nth-child(8),
        .hearings-table-landscape-split.cols-5 th:nth-child(9),
        .hearings-table-landscape-split.cols-5 td:nth-child(9) {
          width: calc((100% - 140pt) / 7);
        }
        
        thead {
          display: table-header-group;
        }
        tbody {
          display: table-row-group;
        }
        tr {
          page-break-inside: avoid;
        }

        .table-header {
          background-color: #e9ecef;
        }
        
        .table-header th {
          padding: 6pt 6pt;
          text-align: left;
          font-weight: bold;
          font-size: 10pt;
          border: 0.5pt solid #dee2e6;
          line-height: 1.2;
        }
        
        tbody tr {
          border: 0.5pt solid #000;
        }
        
        td {
          padding: 6pt 6pt;
          vertical-align: top;
          border: 0.5pt solid #000;
          font-size: 9pt;
          line-height: 1.6;
          word-wrap: break-word;
          overflow-wrap: break-word;
          overflow: hidden;
        }
        
        .no-data {
          padding: 20pt 10pt;
          font-size: 12pt;
          font-weight: normal;
          text-align: center;
          color: #6c757d;
        }

        /* Tasks table specific column widths - matching PDF export */
        /* Tasks: [130, 90, 80, 200] = 500pt */
        .tasks-table th:nth-child(1), .tasks-table td:nth-child(1) { width: 26%; }  /* CNR No - 130pt */
        .tasks-table th:nth-child(2), .tasks-table td:nth-child(2) { width: 18%; }  /* Created At - 90pt */
        .tasks-table th:nth-child(3), .tasks-table td:nth-child(3) { width: 16%; }  /* Status - 80pt */
        .tasks-table th:nth-child(4), .tasks-table td:nth-child(4) { width: 40%; }  /* Task Details - 200pt */

        @media print {
          .footer {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <!-- NEW MODERN HEADER - MATCHES YOUR PDF -->
      <div class="modern-header">
        <div class="header-top">
          <div>
            ${logoImageData
        ? `<img src="${logoImageData}" alt="LawSuit Pro" style="width: 120px; height: 34px; display: block; margin-bottom: 5px;" />`
        : ""
      }
            <div class="header-tagline" style="font-size: 7pt;">www.lawsuit-pro.com/</div>
          </div>
          <div class="header-license">
            Licensed to: ${companyName}
          </div>
        </div>
      </div>

      <!-- MODERN OVERVIEW SECTION -->
      <div class="overview-section">
        <div class="overview-title">Dashboard Overview</div>
        <div class="overview-stats">${totalItems} Total Items  •  ${totalHearings} Hearings  •  ${totalTasks} Tasks</div>
        <div class="overview-date">${scheduleDate}</div>
      </div>
      
      ${noDataHtml}

      ${hearingsSectionsHtml}

    ${(contentType === "tasks" ||
        contentType === "both" ||
        contentType === "custom") &&
        tasksList.length > 0
        ? `
        <div class="task-section-header">
          <span class="section-icon">✓</span>
          Tasks & Internal Matters (${tasksList.length})
        </div>
        <table class="tasks-table">
          <thead class="table-header">
            <tr>
              <th>CNR No</th>
              <th>Created At</th>
              <th>Status</th>
              <th>Task Details</th>
            </tr>
          </thead>
          <tbody>
            ${tasksHtml}
          </tbody>
        </table>
      `
        : ""
      }

    </body>
  </html>
`;

    // Create iframe for printing
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "absolute";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "none";

    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
    if (frameDoc.document) {
      frameDoc.document.open();
      frameDoc.document.write(htmlContent);
      frameDoc.document.close();
    } else {
      frameDoc.open();
      frameDoc.write(htmlContent);
      frameDoc.close();
    }

    // Wait for all images to load before printing
    const waitForImages = () => {
      return new Promise((resolve) => {
        const images =
          printFrame.contentDocument?.querySelectorAll("img") || [];
        if (images.length === 0) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const checkAllLoaded = () => {
          loadedCount++;
          if (loadedCount >= images.length) {
            resolve();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkAllLoaded();
          } else {
            img.onload = checkAllLoaded;
            img.onerror = checkAllLoaded; // Continue even if image fails
          }
        });

        // Fallback timeout in case images take too long
        setTimeout(resolve, 2000);
      });
    };

    // Wait for iframe to be ready, then wait for images, then print
    setTimeout(async () => {
      await waitForImages();

      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();

      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }, 300);
  };

  if (!isOpen) return null;

  const todayData = getTodayData();
  const hasDataToday =
    todayData.tasks.length > 0 || todayData.hearings.length > 0;
  const showAdditionalColumns = contentType === "custom";

  const getContentDescription = () => {
    if (exportType === "selected") {
      if (!selectedDate) return "Please select a date";
      const selectedData = getSelectedDateData();
      const formattedDate = new Date(selectedDate).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      if (contentType === "tasks") {
        return selectedData.tasks.length > 0
          ? `Found ${selectedData.tasks.length} tasks for ${formattedDate}`
          : `No tasks available for ${formattedDate}`;
      } else if (contentType === "hearings") {
        return selectedData.hearings.length > 0
          ? `Found ${selectedData.hearings.length} hearings for ${formattedDate}`
          : `No hearings available for ${formattedDate}`;
      } else if (contentType === "custom") {
        return selectedData.tasks.length > 0 || selectedData.hearings.length > 0
          ? `Found ${selectedData.tasks.length} tasks and ${selectedData.hearings.length} hearings for ${formattedDate} (Custom columns)`
          : `No data available for ${formattedDate}`;
      } else {
        return selectedData.tasks.length > 0 || selectedData.hearings.length > 0
          ? `Found ${selectedData.tasks.length} tasks and ${selectedData.hearings.length} hearings for ${formattedDate}`
          : `No data available for ${formattedDate}`;
      }
    }

    if (contentType === "tasks") {
      return hasDataToday
        ? `Found ${todayData.tasks.length} tasks for today`
        : "No tasks available for today";
    } else if (contentType === "hearings") {
      return hasDataToday
        ? `Found ${todayData.hearings.length} hearings for today`
        : "No hearings available for today";
    } else if (contentType === "custom") {
      return hasDataToday
        ? `Found ${todayData.tasks.length} tasks and ${todayData.hearings.length} hearings for today (Custom columns)`
        : "No data available for today - will export blank page";
    } else {
      return hasDataToday
        ? `Found ${todayData.tasks.length} tasks and ${todayData.hearings.length} hearings for today`
        : "No data available for today - will export blank page";
    }
  };

  return (
    <div className="export-modal-backdrop">
      <div ref={modalRef} className="export-modal">
        <h2 className="export-modal-title">Export Dashboard Data</h2>

        <div className="export-modal-options">
          {/* Today Option */}
          <label className="export-modal-option">
            <input
              type="radio"
              value="today"
              checked={exportType === "today"}
              onChange={(e) => setExportType(e.target.value)}
            />
            <span>Export Today's Data</span>
          </label>
          <p className="export-modal-desc">{getContentDescription()}</p>

          {/* Range Option */}
          <label className="export-modal-option">
            <input
              type="radio"
              value="range"
              checked={exportType === "range"}
              onChange={(e) => setExportType(e.target.value)}
            />
            <span>Export Custom Date Range</span>
          </label>
          <div className="export-modal-range">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={exportType !== "range"}
            />
            <span>to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={exportType !== "range"}
            />
          </div>
          {exportType === "range" && (
            <p className="export-modal-desc">
              Exporting data from <b>{startDate || "--"}</b> to{" "}
              <b>{endDate || "--"}</b>
            </p>
          )}
        </div>

        <div className="export-modal-content-type">
          <h3 className="content-type-title">Content to Export</h3>
          <div className="content-type-options">
            <label className="content-type-option">
              <input
                type="radio"
                value="both"
                checked={contentType === "both"}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Tasks & Hearings</span>
            </label>
            <label className="content-type-option">
              <input
                type="radio"
                value="tasks"
                checked={contentType === "tasks"}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Tasks Only</span>
            </label>
            <label className="content-type-option">
              <input
                type="radio"
                value="hearings"
                checked={contentType === "hearings"}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Hearings Only</span>
            </label>
            <label className="content-type-option">
              <input
                type="radio"
                value="custom"
                checked={contentType === "custom"}
                onChange={(e) => setContentType(e.target.value)}
              />
              <span>Custom</span>
            </label>
          </div>
        </div>

        {/* Additional Hearing Columns Section */}
        {showAdditionalColumns && (
          <div className="additional-columns-section">
            <h3 className="additional-columns-title">
              Additional Hearing Columns
              <span className="subtitle">
                Default: SL No, CR No, Court Name, Case Info - Party Name, CNR, Next Hearing Date & Coram (cannot be deselected)
              </span>
            </h3>
            <div className="additional-columns-grid">
              {availableHearingColumns.map((column) => (
                <label key={column.id} className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={additionalHearingColumns.includes(column.id)}
                    onChange={() => handleAdditionalColumnToggle(column.id)}
                  />
                  <span>{column.label}</span>
                </label>
              ))}
            </div>
            {additionalHearingColumns.length > 0 && (
              <p className="selected-columns-info">
                {additionalHearingColumns.length} additional column
                {additionalHearingColumns.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>
        )}

        {/* Orientation Selection */}
        <div className="export-modal-orientation">
          <h3 className="orientation-title">Page Orientation</h3>
          <div className="orientation-options">
            <label className="orientation-option">
              <input
                type="radio"
                value="portrait"
                checked={orientation === "portrait"}
                onChange={(e) => setOrientation(e.target.value)}
              />
              <span>Portrait</span>
            </label>
            <label className="orientation-option">
              <input
                type="radio"
                value="landscape"
                checked={orientation === "landscape"}
                onChange={(e) => setOrientation(e.target.value)}
              />
              <span>Landscape</span>
            </label>
          </div>
          {additionalHearingColumns.length > 3 &&
            orientation === "portrait" && (
              <p className="orientation-warning">
                ⚠️ Landscape orientation recommended for{" "}
                {additionalHearingColumns.length} additional columns
              </p>
            )}
        </div>

        <div className="export-modal-actions">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-print"
            onClick={handlePrint}
            disabled={exportType === "range" && (!startDate || !endDate)}
          >
            Print
          </button>
          <button
            className="btn-export"
            onClick={handleExport}
            disabled={exportType === "range" && (!startDate || !endDate)}
          >
            Export to PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
